// ShortDateInput component with MM/YY formatting for credit card expiration dates

import { useState, useCallback } from 'react'

interface ShortDateInputProps {
  value: string
  onChange: (value: string) => void
  id?: string
  name?: string
  placeholder?: string
  required?: boolean
  className?: string
}

// Format short date as MM/YY
function formatShortDate(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')

  // Limit to 4 digits
  const limited = digits.slice(0, 4)

  if (limited.length === 0) return ''
  if (limited.length <= 2) return limited
  return `${limited.slice(0, 2)}/${limited.slice(2)}`
}

// Extract raw digits from formatted value
function extractDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 4)
}

export function ShortDateInput({
  value,
  onChange,
  id = 'shortdate',
  name = 'shortdate',
  placeholder = 'MM/YY',
  required = false,
  className = '',
}: ShortDateInputProps) {
  const [displayValue, setDisplayValue] = useState(() => formatShortDate(value))

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    const digits = extractDigits(input)
    const formatted = formatShortDate(digits)

    setDisplayValue(formatted)
    onChange(digits) // Return raw digits to parent
  }, [onChange])

  return (
    <input
      type="text"
      id={id}
      name={name}
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      className={className}
      autoComplete="cc-exp"
      maxLength={5} // MM/YY
      inputMode="numeric"
    />
  )
}
